import { useMemo } from 'react';
import type { StyleParamType } from '@wix/tpa-settings';
import { useSettings, useStyles } from '@wix/tpa-settings/react';
import type { FeaturedBadgePlacement } from 'root/consts';
import { useItemsContext } from 'root/contexts/ItemsContext';
import { stylesParams } from 'root/components/Menus/stylesParams';
import type { StyleParamKeysByType } from 'root/components/Menus/stylesParams';
import { settingsParams } from 'root/components/Menus/settingsParams';
import type { SettingParamValue } from 'root/components/Menus/settingsParams';
import { LayoutStylesParams } from 'root/components/Menus/Settings/Tabs/LayoutTab/LayoutStylesParams';
import { modifiersStylesParams } from 'root/components/Menus/Settings/Tabs/DesignTab/DesignStylesParams';
import { DesignSettingsParams } from 'root/components/Menus/Settings/Tabs/DesignTab/DesignSettingsParams';
import type { IDisplaySettingsParams } from 'root/components/Menus/Settings/Tabs/DisplayTab/DisplaySettingsParams';

type Styles = ReturnType<typeof useStyles>;
type Settings = ReturnType<typeof useSettings>;

// Every styles.get / settings.get re-reads the whole params object, so the values the item subtree
// needs are read once per section instead of once per item, variant, modifier and label.
export const readItemsDisplayValues = (styles: Styles, settings: Settings) => {
  const getSetting = (key: keyof IDisplaySettingsParams) => settings.get(settingsParams[key]!) as SettingParamValue;
  const getHtmlTag = (key: StyleParamKeysByType<StyleParamType.Font>) => styles.get(stylesParams[key]).htmlTag;

  return {
    showItemCurrency: styles.get(stylesParams.showItemCurrency),
    showItemVariantsCurrency: styles.get(stylesParams.showItemVariantsCurrency),
    showItemDiscounts: styles.get(stylesParams.showItemDiscounts),
    showItemVariantsDiscounts: styles.get(stylesParams.showItemVariantsDiscounts),
    showModifierCurrency: styles.get(stylesParams.showModifierCurrency),
    showItemLabelsName: styles.get(stylesParams.showItemLabelsName),
    showItemImage: styles.get(stylesParams.showItemImage),
    showFeaturedBadge: styles.get(stylesParams.showFeaturedBadge),
    featuredBadgePlacement: styles.get(stylesParams.featuredBadgePlacement) as FeaturedBadgePlacement,
    layoutPreset: styles.get(LayoutStylesParams.preset),
    modifiersListView: styles.get(modifiersStylesParams.modifiersListView),
    checkPricingMenus: getSetting('checkPricingMenus'),
    checkPlaceholderMenu: getSetting('checkPlaceholderMenu'),
    imagePlaceholder: getSetting('imagePlaceholder'),
    showZeroPrice: getSetting('showZeroPrice'),
    showZeroPriceToAll: getSetting('showZeroPriceToAll'),
    showImagePlaceholder: getSetting('showImagePlaceholder'),
    showPlaceholderToAll: getSetting('showPlaceholderToAll'),
    showModifierIcon: getSetting('showModifierIcon'),
    overrideItemDesign: settings.get(DesignSettingsParams.overrideItemDesign),
    useLabelIconColor: settings.get(settingsParams.useLabelIconColor),
    itemTitleHtmlTag: getHtmlTag('itemTitleTheme'),
    itemDescriptionHtmlTag: getHtmlTag('itemDescriptionTheme'),
    itemPriceHtmlTag: getHtmlTag('itemPriceTheme'),
    variantTitleHtmlTag: getHtmlTag('variantTitleTheme'),
    variantPriceHtmlTag: getHtmlTag('variantPriceTheme'),
    modifierTitleHtmlTag: getHtmlTag('modifierTheme'),
    modifierGroupTitleHtmlTag: getHtmlTag('modifierGroupTheme'),
  };
};

export type ItemsDisplayValues = ReturnType<typeof readItemsDisplayValues>;

export const useProvideItemsDisplayValues = () => {
  const styles = useStyles();
  const settings = useSettings();

  return useMemo(() => readItemsDisplayValues(styles, settings), [styles, settings]);
};

// Outside of <Items> (tests, stories) there is no provider, so the values are read directly.
export const useItemsDisplayValues = () => {
  const { displayValues } = useItemsContext();
  const styles = useStyles();
  const settings = useSettings();

  return displayValues ?? readItemsDisplayValues(styles, settings);
};
